import React, { useEffect, useMemo, useRef, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Send, Sparkles, Loader2, Trash2, Bot, User as UserIcon, Cpu, Share2, Check, Compass, Coffee, Moon, Users, Shield, Gem, Banknote, MapPin, Sun, History, Plus, X, Mic, MicOff } from 'lucide-react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useCity } from '@/context/CityContext';
import { useNavVisibility } from '@/context/NavVisibilityContext';
import { aiGetSession, aiClearSession, aiChatStream, shareCreate, getPlaces } from '@/lib/api';
import {
  listThreads, ensureActive, createThread, updateThread, deleteThread,
  setActiveId, titleFromMessage,
} from '@/lib/threads';
import useVoiceInput, { voiceSupported } from '@/lib/voice';
import { confirm as hapticConfirm, error as hapticError } from '@/lib/haptics';
import { toast } from 'sonner';

const PROMPTS = [
  "I'm here for 5 hours — what should I do?",
  'Cheap, safe cafes near me',
  'Top scams I should avoid here',
  'Late-night food that locals love',
];

const MODELS = [
  { provider: 'auto',       model: 'auto',                            label: 'Auto',         sub: 'Smart pick' },
  { provider: 'openrouter', model: 'google/gemini-2.5-flash',         label: 'Gemini Flash', sub: 'Fast · cheap' },
  { provider: 'openrouter', model: 'anthropic/claude-sonnet-4.5',     label: 'Claude 4.5',   sub: 'Deep · premium' },
];

const CATEGORIES = [
  { id: 'hidden_gems', label: 'Hidden gems', query: 'tourism', icon: Gem,      img: 'https://images.unsplash.com/photo-1470770841072-f978cf4d019e?crop=entropy&cs=srgb&fm=jpg&q=80&w=800' },
  { id: 'food',        label: 'Food spots',  query: 'cafe',    icon: Coffee,   img: 'https://images.unsplash.com/photo-1414235077428-338989a2e8c0?crop=entropy&cs=srgb&fm=jpg&q=80&w=800' },
  { id: 'free',        label: 'Free places', query: 'park',    icon: Banknote, img: 'https://images.unsplash.com/photo-1597817943458-29324453652c?crop=entropy&cs=srgb&fm=jpg&q=80&w=800' },
  { id: 'nightlife',   label: 'Nightlife',   query: 'bar',     icon: Moon,     img: 'https://images.unsplash.com/photo-1606152536277-5aa1fd33e150?crop=entropy&cs=srgb&fm=jpg&q=80&w=800' },
  { id: 'family',      label: 'Family',      query: 'museum',  icon: Users,    img: 'https://images.unsplash.com/photo-1475503572774-15a45e5d60b9?crop=entropy&cs=srgb&fm=jpg&q=80&w=800' },
  { id: 'safe_solo',   label: 'Solo-safe',   query: 'cafe',    icon: Shield,   img: 'https://images.unsplash.com/photo-1520466809213-7b9a56adcd45?crop=entropy&cs=srgb&fm=jpg&q=80&w=800' },
];

function getOrCreateSessionId() {
  return ensureActive();
}

// Pretty label for the "powered by" badge from a raw model slug or legacy id.
const MODEL_LABELS = {
  'google/gemini-2.5-flash':         'Gemini 2.5 Flash',
  'google/gemini-2.5-flash-lite':    'Gemini 2.5 Flash Lite',
  'google/gemini-2.5-pro':           'Gemini 2.5 Pro',
  'anthropic/claude-sonnet-4.5':     'Claude Sonnet 4.5',
  'anthropic/claude-opus-4.5':       'Claude Opus 4.5',
  'anthropic/claude-haiku-4.5':      'Claude Haiku 4.5',
  'openai/gpt-5':                    'GPT-5',
  'openai/gpt-5.2':                  'GPT-5.2',
  'openai/gpt-4o':                   'GPT-4o',
  'openai/gpt-4o-mini':              'GPT-4o mini',
  'meta-llama/llama-3.3-70b-instruct': 'Llama 3.3 70B',
  'gemini/gemini-3-flash-preview':   'Gemini 3 Flash',
  'anthropic/claude-sonnet-4-5-20250929': 'Claude Sonnet 4.5',
};
function prettyModel(model) {
  if (!model) return '';
  if (MODEL_LABELS[model]) return MODEL_LABELS[model];
  // Slug pattern: "vendor/name" → take last segment, title-case loosely.
  const last = String(model).split('/').pop();
  return last.replace(/[-_]/g, ' ').replace(/\b\w/g, c => c.toUpperCase());
}

export default function TrivlyAI() {
  const { city } = useCity();
  const { hide, show } = useNavVisibility();
  const navigate = useNavigate();
  const location = useLocation();
  const [sessionId, setSessionId] = useState(getOrCreateSessionId);
  const [threads, setThreads] = useState(() => listThreads());
  const [threadsOpen, setThreadsOpen] = useState(false);
  const [messages, setMessages] = useState([]);
  const [lastUsed, setLastUsed] = useState(null); // { provider, model } actually used by backend
  const [input, setInput] = useState('');
  const [sending, setSending] = useState(false);
  const [modelIdx, setModelIdx] = useState(0);
  const [activeCat, setActiveCat] = useState(null);
  const [places, setPlaces] = useState([]);
  const [loadingPlaces, setLoadingPlaces] = useState(false);
  const scrollRef = useRef(null);
  const streamCtrlRef = useRef(null);

  const [theme, setTheme] = useState(() => localStorage.getItem('gt_ai_theme') || 'dark');
  useEffect(() => { localStorage.setItem('gt_ai_theme', theme); }, [theme]);

  const t = useMemo(() => ({
    bg: theme === 'dark' ? 'bg-[#050508]' : 'bg-[#F2F0E4]',
    text: theme === 'dark' ? 'text-white' : 'text-[#0F1A13]',
    textSec: theme === 'dark' ? 'text-[#9CA3AF]' : 'text-[#7A8A80]',
    glass: theme === 'dark' 
      ? 'bg-white/5 border border-white/10 backdrop-blur-xl shadow-[0_8px_32px_rgba(0,0,0,0.3)]' 
      : 'bg-white/40 border border-[#0F1A13]/10 backdrop-blur-xl shadow-[0_8px_32px_rgba(0,0,0,0.05)]',
    iconBtn: theme === 'dark'
      ? 'bg-white/5 border border-white/10 text-[#9CA3AF] hover:text-white hover:bg-white/10'
      : 'bg-[#0F1A13]/5 border border-[#0F1A13]/10 text-[#4A5A50] hover:bg-[#0F1A13]/10',
    titleAccent: theme === 'dark' ? 'text-[#00F0FF] drop-shadow-[0_0_12px_rgba(0,240,255,0.4)]' : 'gt-text-gradient',
    accent: theme === 'dark' ? 'bg-[#00F0FF] text-black shadow-[0_0_16px_rgba(0,240,255,0.4)]' : 'bg-gradient-to-br from-[#C4FF3D] via-[#9be635] to-[#F2A47A] text-[#0F1A13]',
    mesh1: theme === 'dark' ? 'radial-gradient(circle, rgba(0,240,255,0.12), transparent 70%)' : 'radial-gradient(circle, rgba(196,255,61,0.40), transparent 70%)',
    mesh2: theme === 'dark' ? 'radial-gradient(circle, rgba(181,55,242,0.12), transparent 70%)' : 'radial-gradient(circle, rgba(242,164,122,0.35), transparent 70%)',
    cardOverlay: theme === 'dark' 
      ? 'bg-gradient-to-t from-[#050508]/95 via-[#050508]/40 to-transparent'
      : 'bg-gradient-to-t from-[#0F1A13]/85 via-[#0F1A13]/30 to-[#0F1A13]/05',
    cardIcon: theme === 'dark' ? 'bg-black/40 border border-white/20 text-[#00F0FF] shadow-[0_0_10px_rgba(0,240,255,0.2)]' : 'bg-white/15 border border-white/25 text-white',
    cardTitle: theme === 'dark' ? 'text-white font-medium' : 'text-white font-bold',
    userBubble: theme === 'dark' 
      ? 'bg-[#00F0FF]/15 border border-[#00F0FF]/30 text-[#E5E7EB] shadow-[0_0_20px_rgba(0,240,255,0.1)]'
      : 'bg-[#15291C] text-[#C4FF3D] shadow-[0_10px_28px_rgba(15,26,19,0.30)]',
    aiBubble: theme === 'dark'
      ? 'bg-white/5 border border-white/10 text-[#D1D5DB] backdrop-blur-md'
      : 'bg-white/40 border border-[#0F1A13]/10 text-[#0F1A13] backdrop-blur-md shadow-[0_8px_32px_rgba(0,0,0,0.05)]',
    chip: theme === 'dark'
      ? 'bg-white/5 border border-white/10 text-[#E5E7EB] hover:bg-white/10'
      : 'bg-[#0F1A13]/5 border border-[#15291C]/15 text-[#0F1A13] hover:bg-[#C4FF3D]/20',
    chipActive: theme === 'dark'
      ? 'bg-[#00F0FF]/20 border border-[#00F0FF]/40 text-[#00F0FF] shadow-[0_0_12px_rgba(0,240,255,0.2)]'
      : 'bg-gradient-to-r from-[#C4FF3D]/35 to-[#C4FF3D]/15 border-[#15291C]/30 text-[#0F1A13]',
    composer: theme === 'dark'
      ? 'bg-gradient-to-t from-[#050508] via-[#050508] to-transparent'
      : 'bg-gradient-to-t from-[#F2F0E4] via-[#F2F0E4] to-transparent',
    composerInputBox: theme === 'dark'
      ? 'bg-[#1A1A24]/60 border border-white/10 backdrop-blur-2xl shadow-[0_0_30px_rgba(0,0,0,0.5)]'
      : 'bg-white/80 border border-[#0F1A13]/10 backdrop-blur-xl shadow-[0_8px_32px_rgba(0,0,0,0.08)]',
    composerInput: theme === 'dark'
      ? 'text-white placeholder:text-white/40'
      : 'text-[#0F1A13] placeholder:text-[#7A8A80]',
    sendBtn: theme === 'dark'
      ? 'bg-[#00F0FF] text-[#050508] shadow-[0_0_16px_rgba(0,240,255,0.4)] disabled:opacity-50 disabled:shadow-none'
      : 'bg-gradient-to-br from-[#C4FF3D] via-[#9be635] to-[#F2A47A] text-[#0F1A13] shadow-[0_6px_18px_rgba(196,255,61,0.40)] disabled:opacity-50',
    dot: theme === 'dark' ? 'bg-[#00F0FF]' : 'bg-[#C4FF3D]'
  }), [theme]);

  useEffect(() => {
    let alive = true;
    aiGetSession(sessionId)
      .then(d => { if (alive) setMessages(d.messages || []); })
      .catch(() => { if (alive) setMessages([]); });
    return () => { alive = false; };
  }, [sessionId]);

  // Smart welcome: if Landing navigated here with a prefilled question, send it once.
  const prefillSentRef = useRef(false);
  useEffect(() => {
    const prefill = location?.state?.prefill;
    if (!prefill || prefillSentRef.current) return;
    prefillSentRef.current = true;
    // Fire the send + clear the URL state INSIDE the timeout callback so that
    // history.replaceState doesn't retrigger this effect and cancel the timer.
    setTimeout(() => {
      send(prefill);
      try { window.history.replaceState({}, ''); } catch {}
    }, 350);
    // Intentionally NO cleanup — we don't want to cancel the timer if the
    // location.state changes via the replaceState above.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location?.state?.prefill]);

  useEffect(() => {
    if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
  }, [messages, sending]);

  useEffect(() => () => show(), [show]);

  useEffect(() => {
    if (!activeCat) return;
    let alive = true;
    setLoadingPlaces(true);
    const cat = CATEGORIES.find(c => c.id === activeCat) || CATEGORIES[0];
    getPlaces(city.lat, city.lon, cat.query, 2500, 12)
      .then(r => { if (alive) setPlaces(r?.places || r?.items || []); })
      .catch(() => { if (alive) { setPlaces([]); toast.error('Could not load places'); } })
      .finally(() => alive && setLoadingPlaces(false));
    return () => { alive = false; };
  }, [activeCat, city.lat, city.lon]);

  const send = (text) => {
    const msg = (text ?? input).trim();
    if (!msg || sending) return;
    hapticConfirm();
    setInput('');
    setMessages(m => [...m, { role: 'user', content: msg, ts: new Date().toISOString() }]);
    setSending(true);

    const sel = MODELS[modelIdx];
    const payload = {
      session_id: sessionId, message: msg,
      provider: sel.provider, model: sel.model,
      city_name: city?.name, country: city?.country, country_code: city?.country_code,
      lat: city?.lat, lon: city?.lon,
    };

    // Insert placeholder assistant message we will append tokens into.
    setMessages(m => [...m, { role: 'assistant', content: '', ts: new Date().toISOString(), streaming: true }]);

    let acc = '';
    streamCtrlRef.current = aiChatStream(payload, {
      onToken: (delta) => {
        acc += delta;
        setMessages(m => {
          const next = m.slice();
          if (next.length === 0) return m;
          const last = next[next.length - 1];
          if (last.role === 'assistant') {
            next[next.length - 1] = { ...last, content: acc };
          }
          return next;
        });
      },
      onDone: (info) => {
        setMessages(m => {
          const next = m.slice();
          if (next.length && next[next.length - 1].role === 'assistant') {
            next[next.length - 1] = {
              ...next[next.length - 1],
              content: info?.reply || acc,
              streaming: false,
            };
          }
          return next;
        });
        if (info?.provider || info?.model) setLastUsed({ provider: info.provider, model: info.model });
        // Update thread metadata
        try {
          const current = listThreads();
          const exists = current.find(t => t.id === sessionId);
          const titleSrc = exists?.title && exists.title !== 'New chat' ? exists.title : titleFromMessage(msg);
          updateThread(sessionId, { title: titleSrc, last_msg: (info?.reply || acc).slice(0, 90), city_name: city?.name || '' });
          setThreads(listThreads());
        } catch (e) {
          console.warn('Trivly thread update failed:', e);
        }
        setSending(false);
        streamCtrlRef.current = null;
      },
      onError: () => {
        if (acc) {
          // Keep partial reply, mark stream done
          setMessages(m => {
            const next = m.slice();
            if (next.length && next[next.length - 1].role === 'assistant') {
              next[next.length - 1] = { ...next[next.length - 1], streaming: false };
            }
            return next;
          });
        } else {
          // No partial — remove placeholder + user msg, restore input
          setMessages(m => m.slice(0, -2));
          setInput(msg);
        }
        toast.error('AI unavailable. Try again.');
        hapticError();
        setSending(false);
        streamCtrlRef.current = null;
      },
    });
  };

  // Cancel an in-flight stream on unmount or session swap
  useEffect(() => () => { streamCtrlRef.current?.abort(); }, [sessionId]);

  // Thread actions
  const onNewThread = () => {
    hapticConfirm();
    streamCtrlRef.current?.abort();
    const t = createThread(city?.name || '');
    setSessionId(t.id);
    setMessages([]);
    setThreads(listThreads());
    setThreadsOpen(false);
    setLastUsed(null);
  };
  const onPickThread = (id) => {
    if (id === sessionId) { setThreadsOpen(false); return; }
    streamCtrlRef.current?.abort();
    setActiveId(id);
    setSessionId(id);
    setThreadsOpen(false);
    setLastUsed(null);
  };
  const onDeleteThread = async (id, e) => {
    e?.stopPropagation?.();
    try { await aiClearSession(id); } catch {}
    const nextId = deleteThread(id);
    setThreads(listThreads());
    if (nextId !== sessionId) {
      setSessionId(nextId);
      setLastUsed(null);
    }
  };

  const onClear = async () => {
    try {
      await aiClearSession(sessionId);
      setMessages([]);
      updateThread(sessionId, { title: 'New chat', last_msg: '' });
      setThreads(listThreads());
      toast.success('Cleared');
    } catch { toast.error('Could not clear'); }
  };

  const [shareBusy, setShareBusy] = useState(false);
  const [shared, setShared] = useState(false);
  const onShare = async () => {
    const lastAssistant = [...messages].reverse().find(m => m.role === 'assistant');
    if (!lastAssistant) { toast.error('Ask the AI first, then share'); return; }
    const lastUser = [...messages].reverse().find(m => m.role === 'user');
    setShareBusy(true);
    try {
      const title = (lastUser?.content || 'My Trivly plan').slice(0, 80);
      const res = await shareCreate({ title, city_name: city?.name, country: city?.country, body: lastAssistant.content });
      const url = `${window.location.origin}/share/${res.sid}`;
      try {
        const prev = JSON.parse(localStorage.getItem('gt_my_plans') || '[]');
        if (!prev.find(p => p.sid === res.sid)) {
          prev.unshift({ sid: res.sid, title, city_name: city?.name || '', country: city?.country || '', created_at: new Date().toISOString() });
          localStorage.setItem('gt_my_plans', JSON.stringify(prev.slice(0, 50)));
        }
      } catch {}
      try { await navigator.clipboard.writeText(url); } catch {}
      hapticConfirm(); setShared(true);
      toast.success('Share link copied!', { description: url });
      setTimeout(() => setShared(false), 2000);
    } catch {
      hapticError(); toast.error('Could not create share link');
    } finally {
      setShareBusy(false);
    }
  };

  const conversationStarted = messages.length > 0;
  const activeCatObj = useMemo(() => CATEGORIES.find(c => c.id === activeCat), [activeCat]);

  // Voice input — hold-to-speak. Live transcript flows into the composer.
  // Auto-sends the final transcript on stop.
  const voice = useVoiceInput({
    onFinal: (txt) => {
      if (!txt) return;
      send(txt);
    },
  });
  useEffect(() => {
    if (voice.listening || voice.transcript) {
      setInput(voice.transcript);
    }
  }, [voice.listening, voice.transcript]);
  useEffect(() => {
    if (voice.error) {
      toast.error(voice.error);
      hapticError();
    }
  }, [voice.error]);

  return (
    <div data-testid="trivly-screen" className={`relative min-h-dvh pb-2 font-sans transition-colors duration-500 ${t.bg}`}>
      {/* Decorative mesh */}
      <div className="absolute top-0 left-0 right-0 h-[35%] pointer-events-none overflow-hidden" aria-hidden>
        <div className="absolute -top-12 -left-10 w-72 h-72 rounded-full blur-3xl transition-all duration-700" style={{ background: t.mesh1 }} />
        <div className="absolute -top-8 right-0 w-72 h-72 rounded-full blur-3xl transition-all duration-700" style={{ background: t.mesh2 }} />
      </div>

      {/* Header */}
      <div className="relative px-5 pt-6 pb-3 flex items-center justify-between z-10">
        <div className="flex items-center gap-3">
          <div className={`w-11 h-11 rounded-2xl flex items-center justify-center transition-all duration-500 ${t.accent}`}>
            <Sparkles className="w-5 h-5" />
          </div>
          <div>
            <p className={`text-[10.5px] uppercase tracking-[0.22em] font-bold ${theme === 'dark' ? 'text-[#00F0FF]/80' : 'text-[#15291C]'}`}>Your travel mind</p>
            <h1 className="font-display text-2xl font-bold tracking-tight" data-testid="trivly-title">
              <span className={`transition-all duration-500 ${t.titleAccent}`}>Trivly</span><span className={t.text}>AI</span>
            </h1>
            {(() => {
              const sel = MODELS[modelIdx];
              const shown = lastUsed?.model || (sel?.model !== 'auto' ? sel?.model : null);
              const label = shown
                ? prettyModel(shown)
                : (sel?.model === 'auto' ? 'Auto · smart routing' : '');
              if (!label) return null;
              return (
                <p
                  className={`mt-0.5 text-[10px] font-semibold ${theme === 'dark' ? 'text-[#00F0FF]/70' : 'text-[#7A8A80]'}`}
                  data-testid="trivly-model-badge"
                >
                  powered by <span className={`${theme === 'dark' ? 'text-[#00F0FF]' : 'text-[#15291C]'}`}>{label}</span>
                </p>
              );
            })()}
          </div>
        </div>
        <div className="flex items-center gap-2">
          <button
            type="button" onClick={() => { hapticConfirm(); setThreadsOpen(true); }} aria-label="Chat history"
            className={`relative w-9 h-9 rounded-full flex items-center justify-center transition-all duration-300 ${t.iconBtn}`}
            data-testid="trivly-threads-open"
          >
            <History className="w-4 h-4" />
            {threads.length > 1 && (
              <span className={`absolute -top-1 -right-1 text-[9px] font-bold px-1 min-w-[16px] h-[16px] rounded-full flex items-center justify-center ${theme === 'dark' ? 'bg-[#00F0FF] text-black' : 'bg-[#15291C] text-[#C4FF3D]'}`}>
                {threads.length}
              </span>
            )}
          </button>
          <button
            type="button" onClick={onNewThread} aria-label="New chat"
            className={`w-9 h-9 rounded-full flex items-center justify-center transition-all duration-300 ${t.iconBtn}`}
            data-testid="trivly-new-thread"
          >
            <Plus className="w-4 h-4" />
          </button>
          <button
            type="button" onClick={() => { hapticConfirm(); setTheme(theme === 'dark' ? 'light' : 'dark'); }} aria-label="Toggle theme"
            className={`w-9 h-9 rounded-full flex items-center justify-center transition-all duration-300 ${t.iconBtn}`}
            data-testid="trivly-theme-toggle"
          >
            {theme === 'dark' ? <Sun className="w-4 h-4" /> : <Moon className="w-4 h-4" />}
          </button>
          <button
            type="button" onClick={onClear} aria-label="Clear conversation"
            className={`w-9 h-9 rounded-full flex items-center justify-center transition-all duration-300 ${t.iconBtn}`}
            data-testid="trivly-clear"
          >
            <Trash2 className="w-4 h-4" />
          </button>
        </div>
      </div>

      {/* Threads dock */}
      <AnimatePresence>
        {threadsOpen && (
          <>
            <motion.div
              key="thread-backdrop"
              initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
              transition={{ duration: 0.2 }}
              onClick={() => setThreadsOpen(false)}
              className="fixed inset-0 z-40 backdrop-blur-sm bg-black/40"
              data-testid="trivly-threads-backdrop"
            />
            <motion.div
              key="thread-drawer"
              initial={{ x: '-100%' }} animate={{ x: 0 }} exit={{ x: '-100%' }}
              transition={{ type: 'spring', stiffness: 320, damping: 32 }}
              className={`fixed top-0 left-0 bottom-0 w-[82%] max-w-[340px] z-50 flex flex-col ${theme === 'dark' ? 'bg-[#0A0A12] border-r border-white/10' : 'bg-[#FBFAF2] border-r border-[#0F1A13]/10'}`}
              data-testid="trivly-threads-drawer"
            >
              <div className={`flex items-center justify-between px-4 pt-5 pb-3 border-b ${theme === 'dark' ? 'border-white/10' : 'border-[#0F1A13]/10'}`}>
                <div>
                  <p className={`text-[10.5px] uppercase tracking-[0.22em] font-bold ${theme === 'dark' ? 'text-[#00F0FF]/80' : 'text-[#15291C]'}`}>Chats</p>
                  <h2 className={`font-display text-xl font-bold tracking-tight ${t.text}`}>History</h2>
                </div>
                <button
                  type="button" onClick={() => setThreadsOpen(false)} aria-label="Close"
                  className={`w-9 h-9 rounded-full flex items-center justify-center ${t.iconBtn}`}
                  data-testid="trivly-threads-close"
                >
                  <X className="w-4 h-4" />
                </button>
              </div>
              <button
                type="button" onClick={onNewThread}
                className={`mx-4 mt-3 mb-1 flex items-center justify-center gap-2 py-2.5 rounded-full text-sm font-semibold ${t.accent}`}
                data-testid="trivly-threads-new"
              >
                <Plus className="w-4 h-4" /> New chat
              </button>
              <div className="flex-1 overflow-y-auto px-2 py-2" data-testid="trivly-threads-list">
                {threads.length === 0 && (
                  <p className={`px-3 py-6 text-center text-[12.5px] ${t.textSec}`}>No chats yet. Ask something below.</p>
                )}
                {threads.map((th) => {
                  const active = th.id === sessionId;
                  return (
                    <div
                      key={th.id}
                      role="button"
                      tabIndex={0}
                      onClick={() => onPickThread(th.id)}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter' || e.key === ' ') {
                          e.preventDefault();
                          onPickThread(th.id);
                        }
                      }}
                      className={`group w-full text-left px-3 py-2.5 rounded-xl mb-1 flex items-start gap-2 cursor-pointer transition-colors ${active ? (theme === 'dark' ? 'bg-[#00F0FF]/15 border border-[#00F0FF]/30' : 'bg-[#C4FF3D]/25 border border-[#15291C]/20') : (theme === 'dark' ? 'hover:bg-white/5' : 'hover:bg-[#0F1A13]/05')}`}
                      data-testid={`trivly-thread-${th.id}`}
                    >
                      <div className="flex-1 min-w-0">
                        <p className={`text-[13px] font-semibold truncate ${t.text}`}>{th.title || 'New chat'}</p>
                        <p className={`text-[11px] truncate mt-0.5 ${t.textSec}`}>
                          {th.city_name ? `${th.city_name} · ` : ''}{th.last_msg || 'No messages yet'}
                        </p>
                      </div>
                      <button
                        type="button" onClick={(e) => onDeleteThread(th.id, e)} aria-label="Delete chat"
                        className={`opacity-0 group-hover:opacity-100 transition-opacity p-1 rounded-md ${theme === 'dark' ? 'hover:bg-white/10 text-white/60 hover:text-red-400' : 'hover:bg-[#0F1A13]/10 text-[#7A8A80] hover:text-red-600'}`}
                        data-testid={`trivly-thread-delete-${th.id}`}
                      >
                        <Trash2 className="w-3.5 h-3.5" />
                      </button>
                    </div>
                  );
                })}
              </div>
            </motion.div>
          </>
        )}
      </AnimatePresence>

      {/* Destination categories */}
      {!conversationStarted && (
        <div className="relative px-5 pb-3 z-10" data-testid="trivly-destinations">
          <p className={`text-[10.5px] uppercase tracking-[0.22em] font-bold mb-3 flex items-center gap-1.5 ${theme === 'dark' ? 'text-white/60' : 'text-[#15291C]'}`}>
            <Compass className="w-3 h-3" /> Discover {city.name}
          </p>
          <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
            {CATEGORIES.map((c, i) => {
              const Icon = c.icon;
              const isActive = activeCat === c.id;
              return (
                <motion.button
                  key={c.id} type="button"
                  onClick={() => { hapticConfirm(); setActiveCat(isActive ? null : c.id); }}
                  initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.3, delay: i * 0.03 }}
                  className={`relative h-28 rounded-2xl overflow-hidden text-left border transition-all duration-300 ${isActive ? (theme === 'dark' ? 'border-[#00F0FF]/50 shadow-[0_0_20px_rgba(0,240,255,0.2)]' : 'border-[#15291C]/40 shadow-[0_0_18px_rgba(196,255,61,0.40)]') : (theme === 'dark' ? 'border-white/5' : 'border-[#0F1A13]/08')}`}
                  data-testid={`trivly-cat-${c.id}`}
                >
                  <img src={c.img} alt="" className="absolute inset-0 w-full h-full object-cover transition-transform duration-700 hover:scale-110" />
                  <div className={`absolute inset-0 transition-all duration-500 ${t.cardOverlay}`} />
                  <div className="relative h-full p-3 flex flex-col justify-between">
                    <span className={`w-7 h-7 rounded-lg backdrop-blur-md flex items-center justify-center transition-all duration-500 ${t.cardIcon}`}>
                      <Icon className="w-3.5 h-3.5" />
                    </span>
                    <p className={`font-display text-[13px] leading-tight drop-shadow-md ${t.cardTitle}`}>{c.label}</p>
                  </div>
                </motion.button>
              );
            })}
          </div>

          {/* Inline place results */}
          <AnimatePresence>
            {activeCat && (
              <motion.div
                initial={{ opacity: 0, height: 0 }} animate={{ opacity: 1, height: 'auto' }} exit={{ opacity: 0, height: 0 }}
                className="mt-3 flex flex-col gap-2 overflow-hidden"
                data-testid="trivly-places"
              >
                {loadingPlaces && [...Array(3)].map((_, i) => <div key={i} className={`h-16 rounded-xl animate-pulse ${theme === 'dark' ? 'bg-white/5' : 'bg-[#0F1A13]/5'}`} />)}
                {!loadingPlaces && places.length === 0 && (
                  <div className={`p-3 rounded-xl text-[12.5px] border ${t.glass} ${t.textSec}`}>No spots found — try another category.</div>
                )}
                {!loadingPlaces && places.slice(0, 6).map((p, i) => (
                  <div key={p.id || i} className={`p-3 rounded-xl flex items-start gap-3 border transition-all duration-300 ${t.glass}`} data-testid={`trivly-place-${i}`}>
                    <div className={`w-8 h-8 rounded-lg flex items-center justify-center shrink-0 ${theme === 'dark' ? 'bg-[#00F0FF]/10 border border-[#00F0FF]/30' : 'bg-[#C4FF3D]/30 border border-[#15291C]/15'}`}>
                      <MapPin className={`w-3.5 h-3.5 ${theme === 'dark' ? 'text-[#00F0FF]' : 'text-[#15291C]'}`} />
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className={`font-semibold text-[13.5px] truncate ${t.text}`}>{p.name}</p>
                      <p className={`text-[11px] mt-0.5 truncate ${t.textSec}`}>{p.address || p.cuisine || '—'}</p>
                    </div>
                    {typeof p.distance_m === 'number' && (
                      <span className={`px-2 py-0.5 rounded-full text-[10px] font-medium shrink-0 ${theme === 'dark' ? 'bg-white/10 text-white/80' : 'bg-[#0F1A13]/10 text-[#0F1A13]'}`}>
                        {p.distance_m < 1000 ? `${p.distance_m}m` : `${(p.distance_m / 1000).toFixed(1)}km`}
                      </span>
                    )}
                  </div>
                ))}
                {activeCatObj && !loadingPlaces && places.length > 0 && (
                  <button
                    type="button"
                    onClick={() => { setActiveCat(null); send(`Plan my evening around ${activeCatObj.label.toLowerCase()} in ${city.name}`); }}
                    data-testid="trivly-ai-on-cat"
                    className={`mt-1 py-2.5 rounded-full text-sm font-semibold flex items-center justify-center gap-2 transition-all duration-300 ${t.accent}`}
                  >
                    <Sparkles className="w-4 h-4" /> Plan this with AI
                  </button>
                )}
              </motion.div>
            )}
          </AnimatePresence>
        </div>
      )}

      {/* Share CTA */}
      {messages.some(m => m.role === 'assistant') && (
        <div className="relative px-5 pb-1 z-10">
          <button
            type="button" onClick={onShare} disabled={shareBusy}
            className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-[11.5px] font-semibold transition-all duration-300 disabled:opacity-60 ${t.chip}`}
            data-testid="trivly-share-cta"
          >
            {shared ? <Check className="w-3.5 h-3.5" /> : shareBusy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Share2 className="w-3.5 h-3.5" />}
            {shared ? 'Link copied' : shareBusy ? 'Sharing…' : 'Save & share plan'}
          </button>
        </div>
      )}

      {/* Model switch */}
      <div className="px-5 pb-2 flex items-center gap-2 overflow-x-auto no-scrollbar z-10 relative" data-testid="trivly-model-switch">
        {MODELS.map((m, i) => {
          const active = i === modelIdx;
          return (
            <button
              key={m.model} type="button" onClick={() => { hapticConfirm(); setModelIdx(i); }}
              className={`shrink-0 inline-flex items-center gap-2 px-3 py-1.5 rounded-full text-[11.5px] font-semibold transition-all duration-300 ${active ? t.chipActive : t.chip}`}
              data-testid={`trivly-model-${m.model.replace(/[^a-z0-9]+/gi, '-').toLowerCase()}`}
            >
              <Cpu className="w-3 h-3" /> {m.label} <span className={`text-[10px] ${active ? (theme==='dark'?'text-[#00F0FF]/70':'text-[#15291C]/70') : t.textSec}`}>{m.sub}</span>
            </button>
          );
        })}
      </div>

      {/* Messages */}
      <div
        ref={scrollRef}
        className="relative px-5 pt-2 pb-6 flex flex-col gap-4 overflow-y-auto z-10"
        style={{ maxHeight: conversationStarted ? 'calc(100dvh - 280px)' : 'calc(100dvh - 600px)' }}
        data-testid="trivly-messages"
      >
        {!conversationStarted && (
          <div className={`p-4 rounded-2xl border ${t.glass}`} data-testid="trivly-empty">
            <div className={`flex items-center gap-2 ${theme === 'dark' ? 'text-[#00F0FF]' : 'text-[#15291C]'}`}>
              <Bot className="w-4 h-4" />
              <span className="text-[11px] uppercase tracking-[0.22em] font-bold">Hi traveler</span>
            </div>
            <p className={`text-[14px] leading-relaxed mt-2 ${t.text}`}>
              I'm your on-the-ground co-pilot in <span className={`font-semibold ${t.titleAccent}`}>{city.name}</span>. Ask anything or tap a destination above.
            </p>
          </div>
        )}

        <AnimatePresence initial={false}>
          {messages.filter(m => !(m.role === 'assistant' && m.streaming && !m.content)).map((m, i) => (
            <motion.div
              key={`${m.role}-${m.ts || i}`}
              initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.25 }}
              className={`flex gap-2.5 ${m.role === 'user' ? 'justify-end' : 'justify-start'}`}
              data-testid={`trivly-msg-${m.role}-${i}`}
            >
              {m.role === 'assistant' && (
                <div className={`w-8 h-8 rounded-full flex items-center justify-center shrink-0 mt-1 ${t.accent}`}>
                  <Sparkles className="w-4 h-4" />
                </div>
              )}
              <div className={`max-w-[80%] rounded-[20px] px-4 py-3 text-[14.5px] leading-relaxed whitespace-pre-wrap ${m.role === 'user' ? t.userBubble : t.aiBubble}`}>
                {m.content}
              </div>
              {m.role === 'user' && (
                <div className={`w-8 h-8 rounded-full flex items-center justify-center shrink-0 mt-1 border ${theme === 'dark' ? 'bg-white/5 border-white/10 text-white' : 'bg-[#0F1A13]/8 border-[#0F1A13]/15 text-[#0F1A13]'}`}>
                  <UserIcon className="w-4 h-4" />
                </div>
              )}
            </motion.div>
          ))}
        </AnimatePresence>

        {sending && !messages.some(m => m.role === 'assistant' && m.streaming && m.content) && (
          <div className="flex items-center gap-2 ml-10 mt-1" data-testid="trivly-typing">
            <span className={`w-1.5 h-1.5 rounded-full animate-bounce ${t.dot}`} />
            <span className={`w-1.5 h-1.5 rounded-full animate-bounce ${t.dot}`} style={{ animationDelay: '120ms' }} />
            <span className={`w-1.5 h-1.5 rounded-full animate-bounce ${t.dot}`} style={{ animationDelay: '240ms' }} />
          </div>
        )}
      </div>

      {/* Quick prompts */}
      {!conversationStarted && (
        <div className="px-5 pb-3 flex gap-2 overflow-x-auto no-scrollbar z-10 relative" data-testid="trivly-prompts">
          {PROMPTS.map((p, i) => (
            <button
              key={i} type="button" onClick={() => send(p)}
              className={`shrink-0 px-4 py-2 rounded-full text-[12.5px] font-medium transition-all duration-300 ${t.chip}`}
              data-testid={`trivly-prompt-${i}`}
            >
              {p}
            </button>
          ))}
        </div>
      )}

      {/* Composer — inline (not floating); sits at the end of the page above the nav */}
      <div
        className={`px-4 pt-3 pb-2 ${t.composer}`}
        style={{ marginBottom: 'calc(96px + env(safe-area-inset-bottom, 0px))' }}
      >
        <div className={`mx-auto max-w-md px-2 py-2 flex items-center gap-2 rounded-[24px] transition-all duration-500 ${t.composerInputBox}`} data-testid="trivly-composer">
          <input
            value={input}
            onChange={(e) => setInput(e.target.value)}
            onFocus={hide}
            onBlur={() => setTimeout(show, 200)}
            onKeyDown={(e) => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); send(); } }}
            placeholder={voice.listening ? 'Listening…' : `Ask anything about ${city?.name || 'this city'}…`}
            className={`flex-1 bg-transparent px-3 outline-none text-[15px] ${t.composerInput}`}
            data-testid="trivly-input"
          />
          {voiceSupported && (
            <button
              type="button"
              onClick={() => { hapticConfirm(); voice.listening ? voice.stop() : voice.start(); }}
              disabled={sending}
              aria-label={voice.listening ? 'Stop voice input' : 'Start voice input'}
              className={`w-10 h-10 rounded-full flex items-center justify-center transition-all duration-300 ${voice.listening ? (theme === 'dark' ? 'bg-red-500 text-white shadow-[0_0_16px_rgba(239,68,68,0.5)] animate-pulse' : 'bg-red-500 text-white shadow-[0_6px_18px_rgba(239,68,68,0.45)] animate-pulse') : t.iconBtn}`}
              data-testid="trivly-mic"
            >
              {voice.listening ? <MicOff className="w-4 h-4" /> : <Mic className="w-4 h-4" />}
            </button>
          )}
          <button
            type="button" onClick={() => send()} disabled={!input.trim() || sending}
            className={`w-10 h-10 rounded-full flex items-center justify-center transition-all duration-300 ${t.sendBtn}`}
            data-testid="trivly-send" aria-label="Send"
          >
            {sending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4 ml-0.5" />}
          </button>
        </div>
        <p
          className={`mt-2 mx-auto max-w-md text-center text-[10.5px] leading-snug ${theme === 'dark' ? 'text-[#7A8A80]/80' : 'text-[#7A8A80]'}`}
          data-testid="trivly-disclaimer"
        >
          Trivly can make mistakes. Verify visas, prices &amp; safety with official sources.
        </p>
      </div>
    </div>
  );
}